import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { vesselService } from '../services/api';
import { showToast } from '../components/Toaster';
import FavoriteButton from '../components/FavoriteButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'; // eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { Legend } from 'recharts';

const formatNumberWithSpaces = (num: number): string => {
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const getDefaultDateRange = () => {
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setDate(oneYearAgo.getDate() - 365);

  const formatDate = (date: Date) => date.toISOString().split('T')[0];

  return {
    from: formatDate(oneYearAgo),
    to: formatDate(today)
  };
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{label}</p>
        <p style={{ margin: 0, color: '#8884d8' }}>
          Vekt: {formatNumberWithSpaces(payload[0].value)} kg
        </p>
      </div>
    );
  }
  return null;
};

function VesselAnalysis() {
  const { vesselId } = useParams<{ vesselId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const defaultDates = getDefaultDateRange();
  const [fromDate, setFromDate] = useState(defaultDates.from);
  const [toDate, setToDate] = useState(defaultDates.to);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState<string | null>(null);
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [vesselInfo, setVesselInfo] = useState<any>(location.state);

  // Fetch vessel info if not available from navigation state (direct URL access)
  useEffect(() => {
    const fetchVesselInfo = async () => {
      if (!vesselId || vesselInfo) return;

      try {
        const searchResults = await vesselService.search(vesselId, 1);
        if (searchResults && searchResults.length > 0) {
          setVesselInfo(searchResults[0]);
        } else {
          showToast('Fartøy ikke funnet', 'error');
          navigate('/');
        }
      } catch (error) {
        showToast('Feil ved lasting av fartøyinformasjon', 'error');
        navigate('/');
      }
    };

    fetchVesselInfo();
  }, [vesselId]);

  useEffect(() => {
    if (vesselInfo) {
      loadData();
    }
  }, [fromDate, toDate, vesselInfo]);

  // Scroll to specific document when navigating from Latest Catches
  useEffect(() => {
    const scrollToDocument = (location.state as any)?.scrollToDocument;
    if (scrollToDocument && data && !loading) {
      // Wait for DOM to render
      setTimeout(() => {
        const element = document.getElementById(`doc-${scrollToDocument}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Auto-expand the document
          setExpandedDocs(prev => new Set(prev).add(scrollToDocument));
        }
      }, 300);
    }
  }, [data, loading]);

  const loadData = async () => {
    if (!vesselId || !vesselInfo) return;

    setLoading(true);
    try {
      // Use registration mark as unique identifier (prefer registreringsmerke)
      const identifier = vesselInfo.registreringsmerke || vesselInfo.vessel_name;
      const result = await vesselService.getCatch(identifier, fromDate, toDate);
      setData(result);
    } catch (error) {
      showToast('Feil ved lasting av fartøydata. Vennligst prøv igjen.', 'error');
      console.error('Error:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (!vesselId) {
    return (
      <div className="card">
        <p>Ingen fartøy valgt. <a href="/">Gå tilbake til søk</a></p>
      </div>
    );
  }

  return (
    <div>
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: '0', flex: 1 }}>{vesselInfo?.vessel_display || vesselInfo?.vessel_name || 'Fartøyanalyse'}</h2>
        {vesselInfo && (
          <div onClick={(e) => e.stopPropagation()}>
            <FavoriteButton
              type="vessel"
              id={vesselInfo.registreringsmerke || vesselInfo.vessel_name}
              registreringsmerke={vesselInfo.registreringsmerke || vesselInfo.vessel_name}
              name={vesselInfo.vessel_display || vesselInfo.vessel_name}
              size="medium"
            />
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '20px' }}>Velg datoperiode</h3>
        <div className="form-group date-range-group">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label htmlFor="fromDate">Fra dato</label>
            <input
              id="fromDate"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label htmlFor="toDate">Til dato</label>
            <input
              id="toDate"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Laster fartøydata...</div>
      ) : data ? (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Totalvekt (kg)</div>
              <div className="stat-value">{formatNumberWithSpaces(data.total_weight_kg || 0)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Antall registreringer</div>
              <div className="stat-value">{data.daily?.length || 0}</div>
            </div>
          </div>

          {Object.keys(data.species_breakdown || {}).length > 0 && (
            <div className="chart-container">
              <div className="chart-title">Fangst per art {selectedSpecies && `(Filtrert: ${selectedSpecies})`}</div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={Object.entries(data.species_breakdown).map(([species, stats]: [string, any]) => ({
                    species: species.substring(0, 20),
                    fullSpecies: species,
                    weight: stats.weight_kg,
                  }))}
                  onClick={(state: any) => {
                    if (state && state.activeTooltipIndex !== undefined) {
                      const clickedSpecies = Object.entries(data.species_breakdown)[state.activeTooltipIndex]?.[0];
                      if (clickedSpecies) {
                        setSelectedSpecies(selectedSpecies === clickedSpecies ? null : clickedSpecies);
                      }
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="species" style={{ cursor: 'pointer' }} />
                  <YAxis tickFormatter={(value) => formatNumberWithSpaces(value)} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="weight" fill="#8884d8" style={{ cursor: 'pointer' }} />
                </BarChart>
              </ResponsiveContainer>
              {selectedSpecies && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                  <button onClick={() => setSelectedSpecies(null)} style={{ padding: '5px 10px', cursor: 'pointer' }}>
                    Fjern artsfilter
                  </button>
                </div>
              )}
            </div>
          )}

          {data.daily && data.daily.length > 0 && (
            <div className="chart-container">
              <div className="chart-title">Månedlig fangstutvikling</div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(() => {
                    const filtered = data.daily.filter((record: any) => !selectedSpecies || record.species === selectedSpecies);
                    const monthlyData: { [key: string]: number } = {};

                    // Generate all months in date range
                    const start = new Date(fromDate);
                    const end = new Date(toDate);
                    for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
                      const monthKey = d.toISOString().slice(0, 7);
                      monthlyData[monthKey] = 0;
                    }

                    // Aggregate by month
                    filtered.forEach((record: any) => {
                      const monthKey = record.date.slice(0, 7);
                      monthlyData[monthKey] = (monthlyData[monthKey] || 0) + (record.weight_kg || 0);
                    });

                    return Object.entries(monthlyData)
                      .sort((a, b) => a[0].localeCompare(b[0]))
                      .map(([month, weight]) => ({
                        month,
                        weight,
                      }));
                  })()}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => formatNumberWithSpaces(value)} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="weight" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="card">
            <h3 style={{ marginBottom: '15px' }}>Daglige fangstregistreringer {selectedSpecies && `(${selectedSpecies})`}</h3>
            {/* Table Header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '32px 80px 1fr 50px',
                gap: '8px',
                padding: '8px 10px',
                backgroundColor: '#e8e8e8',
                alignItems: 'center',
                fontSize: '0.85em',
                fontWeight: '700',
                color: '#333',
                borderBottom: '2px solid #999'
              }}
            >
              <div></div>
              <div>Dato</div>
              <div>Sted</div>
              <div style={{ textAlign: 'right' }}>Vekt</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
              {(() => {
                const filtered = data.daily.filter((record: any) => !selectedSpecies || record.species === selectedSpecies);
                const grouped = filtered.reduce((acc: any, record: any) => {
                  const docNum = record.dokumentnummer || '-';
                  if (!acc[docNum]) {
                    acc[docNum] = { records: [], weight: 0, value: 0, date: record.date, location: record.landing_municipality || '-' };
                  }
                  acc[docNum].records.push(record);
                  acc[docNum].weight += record.weight_kg || 0;
                  acc[docNum].value += record.value_nok || 0;
                  return acc;
                }, {});

                const sorted = Object.entries(grouped).sort(([, a]: [string, any], [, b]: [string, any]) =>
                  new Date(b.date).getTime() - new Date(a.date).getTime()
                );

                return sorted.map(([docNum, group]: [string, any]) => {
                  const isExpanded = expandedDocs.has(docNum);
                  const toggleExpand = (e: React.MouseEvent | React.TouchEvent) => {
                    e.stopPropagation();
                    const newSet = new Set(expandedDocs);
                    if (isExpanded) {
                      newSet.delete(docNum);
                    } else {
                      newSet.add(docNum);
                    }
                    setExpandedDocs(newSet);
                  };

                  // Group species within this document
                  const speciesGrouped = group.records.reduce((acc: any, record: any) => {
                    const species = record.species || 'N/A';
                    if (!acc[species]) {
                      acc[species] = { weight: 0, value: 0, count: 0 };
                    }
                    acc[species].weight += record.weight_kg || 0;
                    acc[species].value += record.value_nok || 0;
                    acc[species].count += 1;
                    return acc;
                  }, {});

                  return (
                    <div key={docNum} id={`doc-${docNum}`} style={{ borderBottom: '1px solid #ddd' }}>
                      {/* Main collapsed row */}
                      <div
                        onClick={toggleExpand}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '32px 80px 1fr 50px',
                          gap: '8px',
                          padding: '12px 10px',
                          backgroundColor: '#f5f5f5',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontSize: '0.9em'
                        }}
                      >
                        <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                          {isExpanded ? '▼' : '▶'}
                        </div>
                        <div style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {group.records[0]?.date || '-'}
                        </div>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={group.location}>
                          {group.location}
                        </div>
                        <div style={{ textAlign: 'right', fontWeight: '600' }}>
                          {formatNumberWithSpaces(group.weight)}
                        </div>
                      </div>

                      {/* Expanded species rows */}
                      {isExpanded && Object.entries(speciesGrouped).map(([species, stats]: [string, any]) => (
                        <div
                          key={species}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '32px 1fr 50px',
                            gap: '8px',
                            padding: '10px 10px 10px 42px',
                            backgroundColor: '#fafafa',
                            alignItems: 'center',
                            fontSize: '0.85em',
                            color: '#666',
                            borderTop: '1px solid #eee'
                          }}
                        >
                          <div></div>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {species}
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            {formatNumberWithSpaces(stats.weight)}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </>
      ) : (
        <div className="error">Ingen data tilgjengelig for valgt datoperiode.</div>
      )}
    </div>
  );
}

export default VesselAnalysis;
